/**
 * VIP flag categories — shared between the VIP import route (server) and the
 * senior list pill / detail banner (client).
 *
 * The flag itself still lives in the single `qaRequirement` text column as
 * "VIP — <Category>" (optionally followed by ": <CookUnity's own reason>"), so
 * no schema change is needed and pre-category rows ("VIP — <free text>") keep
 * working — they simply parse to no category and render as a bare ⭐ VIP.
 */

export const VIP_CATEGORIES = ["Bags", "Drop Zone", "Address", "Other"] as const;
export type VipCategory = (typeof VIP_CATEGORIES)[number];

export function isVipCategory(value: unknown): value is VipCategory {
  return typeof value === "string" && (VIP_CATEGORIES as readonly string[]).includes(value);
}

/** The qaRequirement text written for a VIP flag. */
export function buildVipRequirementText(category: VipCategory, reason?: string): string {
  const r = (reason ?? "").trim().replace(/\.+$/, "");
  return r ? `VIP — ${category}: ${r}` : `VIP — ${category}`;
}

/**
 * Category encoded in a qaRequirement string, or null when the text predates
 * categories (or is not a VIP flag at all).
 */
export function vipCategoryFromRequirement(requirement: string | null | undefined): VipCategory | null {
  if (!requirement) return null;
  const m = requirement.match(/^VIP\s+—\s+([^:]+?)\s*(?::|$)/);
  if (!m) return null;
  const cat = m[1].trim();
  return isVipCategory(cat) ? cat : null;
}
